import { Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const featuredArticles = articles.filter((a) => a.featured).slice(0, 4);
const recentArticles = articles.slice(0, 6);
const displayCategories = categories.filter((c) => c.slug !== 'all');

export default function Home() {
  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20sacred%20space%20with%20morning%20sunlight%20streaming%20through%20tall%20windows%20onto%20an%20open%20Bible%20on%20a%20wooden%20table%2C%20olive%20branches%20in%20the%20background%2C%20peaceful%20reverent%20atmosphere%2C%20golden%20amber%20light%2C%20spiritual%20and%20contemplative%20mood%2C%20minimalist%20yet%20warm%20composition&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
              alt="牧教师 - 蒙召牧养，装备成长的事奉平台"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full text-center px-4 md:px-6 py-20">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              蒙召牧养，装备成长
            </h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              牧教师是专为教会牧者与教师设计的事奉装备平台，提供讲道资源、门徒训练、灵命成长课程与牧养实践指导，帮助每一位蒙召者在职场与教会中忠心服侍、扎根真道、牧养群羊。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                浏览全部资源
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-lg border border-white/40 text-white font-medium text-sm whitespace-nowrap hover:bg-white/10 transition-colors cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Cards */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                事奉装备主题
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                按照你的需要和呼召，选择适合的装备方向
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {displayCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-6 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  精选文章
                </h2>
                <p className="text-foreground-500 text-sm">
                  编辑推荐的高质量事奉资源
                </p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-50 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={`${article.title} - 牧教师`}
                    />
                  </div>
                  <div className="flex flex-col justify-between flex-1 min-w-0">
                    <div>
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-2 whitespace-nowrap">
                        {article.category}
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Recent Articles */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                最新发布
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                持续更新的事奉装备内容
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={`${article.title} - 牧教师`}
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3 whitespace-nowrap">
                      {article.category}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-primary-500">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              加入事奉装备的行列
            </h2>
            <p className="text-primary-100 text-sm md:text-base mb-8 leading-relaxed">
              订阅我们的更新，第一时间获取最新讲道资源、门训课程和牧养指南。让我们一起忠心服侍、扎根真道。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-background-50 text-primary-600 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
            >
              开始浏览
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '牧教师',
            url: 'https://mujiaoshi.com/',
            description:
              '牧教师是专为教会牧者与教师设计的事奉装备平台，提供讲道资源、门徒训练、灵命成长课程与牧养实践指导。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://mujiaoshi.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '牧教师 — 首页',
            url: 'https://mujiaoshi.com/',
            description:
              '牧教师是专为教会牧者与教师设计的事奉装备平台，提供讲道资源、门徒训练、灵命成长课程与牧养实践指导。',
          }),
        }}
      />
    </>
  );
}